import { type CSSProperties, type ReactNode, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

export type OwnedOverlayPortalProps = {
  /** Element (usually in the iframe document) the portal is anchored into. */
  host: Element;
  /** Inline styles applied to the owned container. */
  containerStyle?: CSSProperties;
  /**
   * The children take pointer input (switches, inputs). Stops pointer events
   * at the container so the host page's own document/body handlers never see
   * them. React's delegated listeners live on this same container and
   * `stopPropagation` doesn't affect listeners on the same node, so React
   * handlers on the children keep working. The block click-capture (document,
   * capture phase) runs before this and already ignores overlay clicks.
   */
  interactive?: boolean;
  children: ReactNode;
};

const INTERACTIVE_EVENTS = [
  'mousedown',
  'mouseup',
  'click',
  'dblclick',
  'pointerdown',
  'pointerup',
  'submit',
] as const;

/**
 * Portal whose container is a node WE create and own inside `host`, instead of
 * portaling React children into `host` directly.
 *
 * Host pages mutate their own DOM (hydration, carousels, re-injected source
 * HTML), and the variant replacer rewrites targets' innerHTML. If such code
 * wipes the children of an element React portals into, React's later unmount
 * calls `removeChild` on a node that is no longer a child and crashes with
 * NotFoundError. With an owned container, React only ever mutates the
 * container's children — a link external code never touches — and a
 * MutationObserver re-attaches the container whenever the host wipes it, so
 * the overlay is guaranteed to stay mounted for as long as the host exists.
 *
 * The container carries `data-tinct-overlay`, so every observer that filters
 * out our own DOM work (dom-revision, variant replacer) ignores it, and the
 * replacer's preserve-across-innerHTML dance keeps it alive.
 */
export function OwnedOverlayPortal({
  host,
  containerStyle,
  interactive = false,
  children,
}: OwnedOverlayPortalProps) {
  // Created detached during render (idempotent; a StrictMode duplicate is
  // never appended and just gets GC'd) — the effect below attaches it.
  const container = useMemo(() => {
    const el = host.ownerDocument.createElement('div');
    el.setAttribute('data-tinct-overlay', '');
    return el;
  }, [host]);

  useEffect(() => {
    host.appendChild(container);

    const observer = new MutationObserver(() => {
      // External code removed the container — put it back. If the host itself
      // left the document, do nothing: the dom-revision observer re-resolves
      // targets and the portal remounts on a fresh element.
      if (container.parentElement !== host && host.isConnected) {
        host.appendChild(container);
      }
    });
    observer.observe(host, { childList: true });

    return () => {
      observer.disconnect();
      container.remove();
    };
  }, [host, container]);

  // Styles are applied in an effect (not at creation) so callers can pass a
  // fresh object literal each render without recreating the container.
  useEffect(() => {
    if (containerStyle) {
      Object.assign(container.style, containerStyle);
    }
  }, [container, containerStyle]);

  useEffect(() => {
    if (!interactive) return;
    const stop = (e: Event) => e.stopPropagation();
    for (const evt of INTERACTIVE_EVENTS) container.addEventListener(evt, stop);
    return () => {
      for (const evt of INTERACTIVE_EVENTS) container.removeEventListener(evt, stop);
    };
  }, [container, interactive]);

  return createPortal(children, container);
}
